import type { Chart, Plugin } from "chart.js";

const DEFAULT_COLOR = "rgba(76, 175, 80, 0.9)";
const DEFAULT_WIDTH = 1;
const DEFAULT_DASH_PATTERN = [4, 3];

export interface IMarkerPassageLinesOptions {
  enabled?: boolean;
  xValuesByDataset?: readonly (readonly number[])[];
  xOffset?: number;
  lineColor?: string;
  lineWidth?: number;
  dashPattern?: number[];
}

export const MarkerPassageLinesPlugin: Plugin<"line"> = {
  id: "markerPassageLines",

  afterDraw(chartInstance) {
    const chart = chartInstance as Chart<"line">;
    const pluginOptions = (chart.options as { plugins?: { markerPassageLines?: IMarkerPassageLinesOptions } }).plugins;
    const options = pluginOptions?.markerPassageLines;

    if (!options?.enabled || !options.xValuesByDataset?.length) {
      return;
    }

    const xScale = chart.scales.x;
    const { chartArea, ctx } = chart;

    if (!xScale || typeof xScale.getPixelForValue !== "function" || !chartArea) {
      return;
    }

    const {
      xOffset = 0,
      lineColor = DEFAULT_COLOR,
      lineWidth = DEFAULT_WIDTH,
      dashPattern = DEFAULT_DASH_PATTERN,
    } = options;
    const visibleXValues = new Set<number>();

    options.xValuesByDataset.forEach((xValues, datasetIndex) => {
      const dataset = chart.data.datasets[datasetIndex];

      if (!dataset) {
        return;
      }

      const datasetMeta = chart.getDatasetMeta(datasetIndex);

      if (dataset.hidden === true || datasetMeta.hidden === true) {
        return;
      }

      xValues.forEach(xValue => {
        const shiftedX = xValue + datasetIndex * xOffset;
        const pixelX = xScale.getPixelForValue(shiftedX);

        if (Number.isFinite(pixelX) && pixelX >= chartArea.left && pixelX <= chartArea.right) {
          visibleXValues.add(pixelX);
        }
      });
    });

    if (visibleXValues.size === 0) {
      return;
    }

    ctx.save();
    ctx.setLineDash(dashPattern);
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = lineColor;
    ctx.beginPath();
    visibleXValues.forEach(pixelX => {
      ctx.moveTo(pixelX, chartArea.top);
      ctx.lineTo(pixelX, chartArea.bottom);
    });
    ctx.stroke();
    ctx.restore();
  },
};

export default MarkerPassageLinesPlugin;
